import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { getFeedback, getFeedbackStats, refreshFeedback } from '@/features/feedback/api';
import {
  FeedbackTriageStatusSchema,
  type CustomerFeedback,
  type FeedbackTriageStatus,
} from '@/features/feedback/types';
import { FeedbackTable } from '@/features/feedback/components/FeedbackTable';
import { FeedbackDetailSheet } from '@/features/feedback/components/FeedbackDetailSheet';

import { ListPageShell } from '@/components/layout/ListPageShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useAppSelector } from '@/store/hooks';
import { hasPermission } from '@/lib/auth/permissions';
import { isSuperAdmin } from '@/lib/auth/roles';
import { Loader2, RefreshCw, Search } from '@/lib/icons';
import { DatePickerInput } from '@/components/ui/date-picker-input';

const PAGE_SIZE = 25;

/**
 * Filters live in the URL so a queue is shareable and survives a reload — an
 * operator handing "the new complaints from this week" to a colleague should
 * be able to send a link, not a description. Same convention as /support.
 */
const searchSchema = z.object({
  triageStatus: FeedbackTriageStatusSchema.optional(),
  providerName: z.string().optional(),
  feedbackType: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  unmatched: z.boolean().optional(),
  q: z.string().optional(),
  page: z.number().min(1).optional(),
});

export const Route = createFileRoute('/_layout/feedback/')({
  validateSearch: searchSchema,
  component: FeedbackInboxPage,
});

function StatTile({
  label,
  value,
  tone = 'default',
  active,
  onClick,
}: Readonly<{
  label: string;
  value: number;
  tone?: 'default' | 'warn';
  active?: boolean;
  onClick?: () => void;
}>) {
  const toneClass =
    tone === 'warn' ? 'text-[var(--mz-warn-700)]' : 'text-foreground';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={!!active}
      className={`flex-1 rounded-lg border px-4 py-3 text-left transition-colors ${
        active
          ? 'border-brand-500 bg-brand-50'
          : 'border-border-light bg-card hover:bg-surface-container-low/60'
      }`}
    >
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-bold tabular-nums ${toneClass}`}>
        {value}
      </div>
    </button>
  );
}

function FeedbackInboxPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const user = useAppSelector((state) => state.auth.user);
  const canTriage = hasPermission(user, 'feedback', 'update');
  // The unmatched queue has no tenant to scope by, so the API admits only
  // platform operators. Hiding the control for everyone else is kinder than
  // letting them click into a 403.
  const canSeeUnmatched = isSuperAdmin(user);

  const [selected, setSelected] = useState<CustomerFeedback | null>(null);
  const [searchInput, setSearchInput] = useState(search.q ?? '');

  // Debounce so typing an IMEI does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchInput.trim() || undefined;
      if (next !== search.q) {
        navigate({
          to: '/feedback',
          search: (prev) => ({ ...prev, q: next, page: 1 }),
        });
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput, search.q, navigate]);

  const page = search.page ?? 1;

  const filters = useMemo(
    () => ({
      triageStatus: search.triageStatus,
      providerName: search.providerName,
      feedbackType: search.feedbackType,
      from: search.from,
      to: search.to,
      unmatched: search.unmatched,
      search: search.q,
      skip: (page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
    }),
    [
      search.triageStatus,
      search.providerName,
      search.feedbackType,
      search.from,
      search.to,
      search.unmatched,
      search.q,
      page,
    ]
  );

  const { data: result, isLoading } = useQuery({
    queryKey: ['feedback', filters],
    queryFn: () => getFeedback(filters),
    staleTime: 15_000,
  });

  const { data: statsResult } = useQuery({
    queryKey: ['feedback-stats'],
    queryFn: () => getFeedbackStats(),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const refresh = useMutation({
    mutationFn: () => refreshFeedback(),
    onSuccess: (res) => {
      if (!res.succeeded) {
        toast({
          variant: 'destructive',
          title: t('common.error'),
          description: res.errors?.[0] ?? t('feedback.toast.refreshFailed'),
        });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-stats'] });
      const inserted = res.value?.inserted ?? 0;
      // Say what actually happened. "Refreshed" after a pull that found
      // nothing reads as a failure; "no new feedback" reads as an answer.
      toast({
        title: t('common.success'),
        description:
          inserted > 0
            ? t('feedback.toast.refreshedWithNew', { count: inserted })
            : t('feedback.toast.refreshedNoNew'),
      });
    },
    onError: () =>
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: t('feedback.toast.refreshFailed'),
      }),
  });

  const rows = result?.succeeded ? (result.value ?? []) : [];
  const stats = statsResult?.succeeded ? statsResult.value : undefined;

  const setFilter = <K extends keyof typeof search>(
    key: K,
    value: (typeof search)[K]
  ) =>
    navigate({
      to: '/feedback',
      // Toggle: clicking the active filter clears it, so a filter can always
      // be undone with the control that set it.
      search: (prev) => ({
        ...prev,
        [key]: prev[key] === value ? undefined : value,
        page: 1,
      }),
    });

  const hasFilters =
    !!search.triageStatus ||
    !!search.providerName ||
    !!search.feedbackType ||
    !!search.from ||
    !!search.to ||
    !!search.unmatched ||
    !!search.q;

  return (
    <>
      <ListPageShell
        title={t('feedback.title')}
        description={t('feedback.subtitle')}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending || !canTriage}
            // Refreshing writes rows, so it needs `update`, not `read` — the
            // same rule the API enforces.
            title={canTriage ? undefined : t('feedback.refreshNeedsPermission')}
          >
            {refresh.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="ml-2">{t('feedback.refresh')}</span>
          </Button>
        }
        filters={
          <>
            <div className="relative min-w-[260px] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('feedback.searchPlaceholder')}
                className="pl-9"
                aria-label={t('feedback.searchPlaceholder')}
              />
            </div>

            <select
              value={search.triageStatus ?? ''}
              onChange={(e) =>
                setFilter(
                  'triageStatus',
                  (e.target.value || undefined) as
                    | FeedbackTriageStatus
                    | undefined
                )
              }
              aria-label={t('feedback.filter.status')}
              className="h-9 rounded-md border border-border bg-card px-3 text-sm"
            >
              <option value="">{t('feedback.filter.allStatuses')}</option>
              <option value="new">{t('feedback.triage.new')}</option>
              <option value="in_review">{t('feedback.triage.inReview')}</option>
              <option value="resolved">{t('feedback.triage.resolved')}</option>
            </select>

            <DatePickerInput
              compact
              className="w-40"
              value={search.from ?? ''}
              onChange={(value) =>
                navigate({
                  to: '/feedback',
                  search: (prev) => ({
                    ...prev,
                    from: value || undefined,
                    page: 1,
                  }),
                })
              }
              placeholder={t('feedback.filter.from')}
              aria-label={t('feedback.filter.from')}
            />
            <DatePickerInput
              compact
              className="w-40"
              value={search.to ?? ''}
              onChange={(value) =>
                navigate({
                  to: '/feedback',
                  search: (prev) => ({
                    ...prev,
                    to: value || undefined,
                    page: 1,
                  }),
                })
              }
              placeholder={t('feedback.filter.to')}
              aria-label={t('feedback.filter.to')}
            />

            {canSeeUnmatched ? (
              <Button
                variant={search.unmatched ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('unmatched', true)}
              >
                {t('feedback.filter.unmatched')}
              </Button>
            ) : null}

            {hasFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate({ to: '/feedback', search: {} })}
              >
                {t('feedback.filter.clear')}
              </Button>
            ) : null}
          </>
        }
        hasData
      >
        {/* Queue counters double as one-click filters — the number an operator
            is looking at is the queue they want to open. */}
        {stats ? (
          <div className="flex flex-wrap gap-3 border-b border-border-light px-4 py-4 md:px-5">
            <StatTile
              label={t('feedback.triage.new')}
              value={stats.new}
              tone="warn"
              active={search.triageStatus === 'new'}
              onClick={() => setFilter('triageStatus', 'new')}
            />
            <StatTile
              label={t('feedback.triage.inReview')}
              value={stats.inReview}
              active={search.triageStatus === 'in_review'}
              onClick={() => setFilter('triageStatus', 'in_review')}
            />
            <StatTile
              label={t('feedback.triage.resolved')}
              value={stats.resolved}
              active={search.triageStatus === 'resolved'}
              onClick={() => setFilter('triageStatus', 'resolved')}
            />
            {canSeeUnmatched ? (
              <StatTile
                label={t('feedback.unmatched')}
                value={stats.unmatched}
                tone="warn"
                active={!!search.unmatched}
                onClick={() => setFilter('unmatched', true)}
              />
            ) : null}
          </div>
        ) : null}

        <FeedbackTable
          rows={rows}
          isLoading={isLoading}
          onSelect={setSelected}
        />

        {/* The list endpoint returns a page, not a total. A full page means
            there may be more, so Next is offered only then — same convention
            as /support, and it avoids a count query per keystroke. */}
        {rows.length > 0 ? (
          <div className="flex items-center justify-between border-t border-border-light px-4 py-3 md:px-5">
            <span className="text-sm text-muted-foreground">
              {t('feedback.showing', { count: rows.length })}
              {page > 1 ? ` · ${t('feedback.pageN', { page })}` : ''}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() =>
                  navigate({
                    to: '/feedback',
                    search: (prev) => ({ ...prev, page: page - 1 }),
                  })
                }
              >
                {t('common.actions.previous')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={rows.length < PAGE_SIZE}
                onClick={() =>
                  navigate({
                    to: '/feedback',
                    search: (prev) => ({ ...prev, page: page + 1 }),
                  })
                }
              >
                {t('common.actions.next')}
              </Button>
            </div>
          </div>
        ) : null}
      </ListPageShell>

      <FeedbackDetailSheet
        feedback={selected}
        open={!!selected}
        onOpenChange={(open) => !open && setSelected(null)}
        canTriage={canTriage}
      />
    </>
  );
}
